import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { ACCESS_DENIED_ERROR_CODE } from "../domain/valueList";
import { applyPendingVisibility, isDisplayed, } from "../domain/valueListValue";
import { fetchValueListValues, saveValues, saveValueVisibility } from "../infrastructure/api/valueListApi";
/** The values of the one list on screen, and the display, order and pin changes not saved yet. */
export const useValueListValuesStore = defineStore("valueListValues", () => {
    const values = ref([]);
    const isLoading = ref(false);
    const loadError = ref(false);
    /** The last load was refused because the user no longer manages the list: the screen leaves it. */
    const accessLost = ref(false);
    const pending = ref({});
    const pendingOrder = ref({});
    const pendingPins = ref({});
    /** The values the user moved, restored or pinned; a move also renumbers the values it passes. */
    const touched = ref(new Set());
    let latestLoad = 0;
    const hasPending = computed(() => Object.keys(pending.value).length + Object.keys(pendingOrder.value).length + Object.keys(pendingPins.value).length >
        0);
    /** The pending changes the user made, by kind; a value only renumbered by another one's move is not one. */
    const pendingSummary = computed(() => {
        const isTouched = (internalCode) => touched.value.has(internalCode);
        return {
            visibility: Object.keys(pending.value),
            order: Object.keys(pendingOrder.value).filter(isTouched),
            pins: Object.keys(pendingPins.value).filter(isTouched),
        };
    });
    /** How many values the user changed: a value both moved and hidden counts once. */
    const pendingCount = computed(() => {
        const { visibility, order, pins } = pendingSummary.value;
        const changed = new Set([...visibility, ...order, ...pins]).size;
        // A renumbering can outlive the move that caused it (the value moved back, its neighbours not).
        return hasPending.value ? Math.max(1, changed) : 0;
    });
    /** What the screen shows: the stored values with the pending changes applied. */
    const effectiveValues = computed(() => applyPendingVisibility(values.value, pending.value).map((value) => {
        const sortOrder = pendingOrder.value[value.internalCode];
        const pinRank = pendingPins.value[value.internalCode];
        if (sortOrder === undefined && pinRank === undefined)
            return value;
        return {
            ...value,
            sortOrder: sortOrder ?? value.sortOrder,
            pinRank: pinRank === undefined ? value.pinRank : pinRank,
        };
    }));
    /**
     * Only the latest call writes the state, and the previous list's values are cleared first, so
     * opening list B never shows list A's values, even when A's answer arrives last.
     */
    async function load(nextCode) {
        const thisLoad = ++latestLoad;
        values.value = [];
        pending.value = {};
        pendingOrder.value = {};
        pendingPins.value = {};
        touched.value = new Set();
        isLoading.value = true;
        loadError.value = false;
        accessLost.value = false;
        try {
            const next = await fetchValueListValues(nextCode);
            if (thisLoad === latestLoad)
                values.value = next;
        }
        catch (error) {
            if (thisLoad !== latestLoad)
                return;
            if (getApiErrorCode(error) === ACCESS_DENIED_ERROR_CODE) {
                accessLost.value = true;
                return;
            }
            logger.error("Failed to load the values of a value list", error);
            loadError.value = true;
        }
        finally {
            if (thisLoad === latestLoad)
                isLoading.value = false;
        }
    }
    /** The targets of the save in flight: once it lands they are the stored state. */
    let inFlight = {};
    let lastAvailableDefault = "Visible";
    const withoutNoOps = (entries, baseline) => Object.fromEntries(Object.entries(entries).filter(([code, displayed]) => baseline(code) !== displayed));
    function storedDisplayed(internalCode) {
        const stored = values.value.find((value) => value.internalCode === internalCode);
        return stored ? isDisplayed(stored, lastAvailableDefault) : undefined;
    }
    /**
     * Records a target display state; going back to the state the list will hold drops the change.
     * That state is the stored one, or the target of the save in flight for a value it carries.
     */
    function setDisplayed(internalCode, displayed, availableDefault) {
        lastAvailableDefault = availableDefault;
        if (storedDisplayed(internalCode) === undefined)
            return;
        pending.value = withoutNoOps({ ...pending.value, [internalCode]: displayed }, (code) => inFlight[code] ?? storedDisplayed(code));
    }
    /** The order and pin changes `next` holds against the stored values: a value back where it is stored drops out. */
    function layoutDiff(next) {
        const stored = new Map(values.value.map((value) => [value.internalCode, value]));
        const order = {};
        const pins = {};
        for (const value of next) {
            const before = stored.get(value.internalCode);
            if (!before)
                continue;
            if (value.sortOrder !== before.sortOrder)
                order[value.internalCode] = value.sortOrder;
            if (value.pinRank !== before.pinRank)
                pins[value.internalCode] = value.pinRank;
        }
        return { order, pins };
    }
    /**
     * Records the order and pins of `next` (the effective values after a move, a pin or a restore);
     * `actedOn` are the values the user moved or pinned, as opposed to the ones renumbered around them.
     */
    function setLayout(next, actedOn = []) {
        const { order, pins } = layoutDiff(next);
        pendingOrder.value = order;
        pendingPins.value = pins;
        touched.value = new Set([...touched.value, ...actedOn].filter((internalCode) => internalCode in order || internalCode in pins));
    }
    /**
     * Saves every pending change in one call; rejects (and keeps them pending) on a refusal. A
     * change applied while the call runs is not in it, so it stays pending.
     */
    async function savePending(code) {
        const sent = { ...pending.value };
        const changes = {
            visibility: Object.entries(sent).map(([internalCode, displayed]) => ({ internalCode, isHidden: !displayed })),
            order: Object.entries(pendingOrder.value).map(([internalCode, sortOrder]) => ({ internalCode, sortOrder })),
            pins: Object.entries(pendingPins.value).map(([internalCode, pinRank]) => ({ internalCode, pinRank })),
        };
        if (changes.visibility.length + changes.order.length + changes.pins.length === 0)
            return;
        inFlight = sent;
        try {
            // CONSTRAINT: a save with no move and no pin keeps the visibility endpoint, so showing or
            // hiding a value works whatever the deploy order of the BFF and OPCAT that bring values/batch.
            const changed = changes.order.length + changes.pins.length === 0
                ? await saveValueVisibility(code, changes.visibility)
                : await saveValues(code, changes);
            const byCode = new Map(changed.map((value) => [value.internalCode, value]));
            // The effective layout is read before the stored values move, so a move made during the
            // call is still measured against what will be stored.
            const layout = effectiveValues.value;
            values.value = values.value.map((value) => byCode.get(value.internalCode) ?? value);
            setLayout(layout);
            pending.value = withoutNoOps(pending.value, (internalCode) => sent[internalCode]);
        }
        finally {
            inFlight = {};
            // On a refusal the stored state did not move: a change undone during the call is a no-op again.
            pending.value = withoutNoOps(pending.value, storedDisplayed);
        }
    }
    /** Drops every change not saved yet, e.g. when the list can no longer be managed. */
    function discardPending() {
        pending.value = {};
        pendingOrder.value = {};
        pendingPins.value = {};
        touched.value = new Set();
    }
    return {
        values,
        effectiveValues,
        isLoading,
        loadError,
        accessLost,
        pending,
        pendingOrder,
        pendingPins,
        pendingSummary,
        pendingCount,
        hasPending,
        load,
        setDisplayed,
        setLayout,
        savePending,
        discardPending,
    };
});
